import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { fromEvent, Observable, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import * as FileSaver from 'file-saver';

import { ConfigService } from '../config/config.service';

import { DownloadFormat, HttpMethod } from './download.enumeration';

export { DownloadFormat, HttpMethod };

@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    downloadSubject: Subject<Response> = new Subject<Response>();

    constructor(private http: HttpClient, private configService: ConfigService) {}

    // #region Retrieve Download Service Call

    RetrieveDownload(
        httpMethod: HttpMethod,
        ServiceURL: string,
        Parameters: any,
        Format: DownloadFormat
    ) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'Pathways',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const queryString = Object.keys(Parameters)
                .reduce((prev: string[], cur: string) => {
                    prev.push(`${encodeURIComponent(cur)}=${encodeURIComponent(Parameters[cur] || '')}`);

                    return prev;
                }, [])
                .join('&');

            const url = this.configService.hostAddress + `${ServiceURL}?${queryString}`;

            responseObs = this.http.get(url, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + ServiceURL, Parameters, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp: any) => {
                const blob = resp.body;

                const contentdistribution = resp.headers?.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                FileSaver.saveAs(blob, filename);

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    return error;
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Retrieve File Service Call

    RetrieveFile(
        httpMethod: HttpMethod, 
        FileURL: string, 
        Format: DownloadFormat, 
        Filename?: string
    ) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'Pathways',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const url = this.configService.hostAddress + `${FileURL}`;

            responseObs = this.http?.get(url, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + FileURL, null, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp: any) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = Filename || 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                FileSaver.saveAs(blob, filename);

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    return error;
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Run Report Service Call

    RunReport(
        AuthToken: string,
        CurrentRole: string,
        ReportID: number,
        ReportType: DownloadFormat,
        ReportFileName: string,
        ApplicationSchoolYear: string,
        UseDateRestriction: boolean,
        DateBegin: Date,
        DateEnd: Date,
        DateRestrictionType: number,
        AsDistrictOffice: boolean
    ) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'Pathways',
        });

        let extension = '';

        switch (ReportType) {
            case DownloadFormat.PDF: // PDF
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.Excel: // Excel
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        responseObs = this.http.post(
            this.configService.hostAddress + 'Api/Services/Report/RunReport',
            {
                AuthToken,
                CurrentRole,
                ReportID,
                ApplicationSchoolYear,
                UseDateRestriction,
                DateBegin: DateBegin != null ? DateBegin.toJSON() : null,
                DateEnd: DateEnd != null ? DateEnd.toJSON() : null,
                DateRestrictionType,
                AsDistrictOffice,
                Attachment: true,
            },
            { headers, observe: 'response', responseType: 'blob' }
        );

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp: any) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                FileSaver.saveAs(blob, ReportFileName);

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    return error;
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion
}
